import React, { useState } from 'react';
import { X, Download, FileText, FileSpreadsheet, File, Database, BarChart3 } from 'lucide-react';
import { exportToExcel, exportToCSV, exportToPDF, exportToJSON, getExportStats } from '../../utils/exportUtils';
import { toast } from 'react-toastify';
import './ExportModal.scss';

const ExportModal = ({ isOpen, onClose, appointments, userNotes, filteredAppointments }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState('excel');
  const [exportScope, setExportScope] = useState('filtered'); // 'all' or 'filtered'
  const [customFileName, setCustomFileName] = useState('');

  if (!isOpen) return null;

  const dataToExport = exportScope === 'all' ? appointments : filteredAppointments;
  const stats = getExportStats(dataToExport, userNotes);

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const filename = customFileName.trim() || 'mostre_export';
      let result;

      switch (exportType) {
        case 'excel':
          result = exportToExcel(dataToExport, userNotes, filename);
          break;
        case 'csv':
          result = exportToCSV(dataToExport, userNotes, filename);
          break;
        case 'pdf':
          result = exportToPDF(dataToExport, userNotes, filename);
          break;
        case 'json':
          result = exportToJSON(dataToExport, userNotes, filename);
          break;
        default:
          result = { success: false, message: 'Format de export necunoscut!' };
      }

      if (result.success) {
        toast.success(result.message);
        onClose();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Eroare la exportul datelor!');
    } finally {
      setIsExporting(false);
    }
  };

  const exportOptions = [
    {
      id: 'excel',
      name: 'Excel (XLSX)',
      description: 'Format profesional pentru analize și rapoarte complexe',
      icon: <FileSpreadsheet size={20} />,
      color: '#217346',
      features: ['Formatare avansată', 'Grafice și tabele', 'Compatibilitate universală']
    },
    {
      id: 'csv',
      name: 'CSV',
      description: 'Format simplu și universal pentru import în orice aplicație',
      icon: <FileText size={20} />,
      color: '#0078d4',
      features: ['Compatibilitate maximă', 'Dimensiune mică', 'Editare rapidă']
    },
    {
      id: 'pdf',
      name: 'PDF',
      description: 'Document finalizat pentru prezentări și arhivare',
      icon: <File size={20} />,
      color: '#d83b01',
      features: ['Format fix', 'Aspect profesional', 'Partajare facilă']
    },
    {
      id: 'json',
      name: 'JSON (Backup)',
      description: 'Backup complet pentru dezvoltatori și restaurare avansată',
      icon: <Database size={20} />,
      color: '#5c2d91',
      features: ['Date complete', 'Pentru dezvoltatori', 'Restaurare precisă']
    }
  ];

  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        <div className="export-modal-header">
          <div className="header-content">
            <div className="header-icon-wrapper">
              <Download size={28} />
            </div>
            <div className="header-text">
              <h2>Exportă Date Mostre</h2>
              <p>Selectează formatul și configurațiile pentru export</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="export-modal-body">
          {/* Export Statistics */}
          <div className="export-stats">
            <div className="stats-header">
              <BarChart3 size={18} />
              <span>Statistici Export</span>
            </div>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Total înregistrări:</span>
                <span className="stat-value">{stats.total}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Înscriși:</span>
                <span className="stat-value enrolled">{stats.enrolled}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Cu provocări:</span>
                <span className="stat-value">{stats.withChallenges}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Cu mostre:</span>
                <span className="stat-value">{stats.withSamples}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Campanii:</span>
                <span className="stat-value">{stats.campaigns}</span>
              </div>
            </div>
          </div>

          {/* Export Scope Selection */}
          <div className="export-scope">
            <label className="section-label">Date de exportat:</label>
            <div className="scope-options">
              <label className="scope-option">
                <input
                  type="radio"
                  name="exportScope"
                  value="filtered"
                  checked={exportScope === 'filtered'}
                  onChange={(e) => setExportScope(e.target.value)}
                />
                <span className="scope-label">
                  <strong>Date filtrate</strong>
                  <small>({filteredAppointments.length} înregistrări)</small>
                </span>
              </label>
              <label className="scope-option">
                <input
                  type="radio"
                  name="exportScope"
                  value="all"
                  checked={exportScope === 'all'}
                  onChange={(e) => setExportScope(e.target.value)}
                />
                <span className="scope-label">
                  <strong>Toate datele</strong>
                  <small>({appointments.length} înregistrări)</small>
                </span>
              </label>
            </div>
          </div>

          {/* Export Format Selection */}
          <div className="export-format">
            <label className="section-label">Alege formatul de export:</label>
            <div className="format-options">
              {exportOptions.map((option) => (
                <label key={option.id} className="format-option">
                  <input
                    type="radio"
                    name="exportType"
                    value={option.id}
                    checked={exportType === option.id}
                    onChange={(e) => setExportType(e.target.value)}
                  />
                  <div className="format-content">
                    <div className="format-header">
                      <div className="format-icon" style={{ color: option.color }}>
                        {option.icon}
                      </div>
                      <div className="format-title">
                        <strong>{option.name}</strong>
                        <span className="format-badge" style={{ backgroundColor: option.color }}>
                          {option.id.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="format-info">
                      <p className="format-description">{option.description}</p>
                      <div className="format-features">
                        {option.features.map((feature, index) => (
                          <span key={index} className="feature-tag">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Custom Filename */}
          <div className="export-filename">
            <label className="section-label">Nume fișier (opțional):</label>
            <input
              type="text"
              placeholder="mostre_export"
              value={customFileName}
              onChange={(e) => setCustomFileName(e.target.value)}
              className="filename-input"
            />
            <small className="filename-note">
              Data va fi adăugată automat la sfârșitul numelui
            </small>
          </div>
        </div>

        <div className="export-modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Anulează
          </button>
          <button 
            className="export-button" 
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <div className="loading-spinner"></div>
                Se exportă...
              </>
            ) : (
              <>
                <Download size={16} />
                Exportă {exportOptions.find(opt => opt.id === exportType)?.name}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
